/**
 * The visible part of the pointer: mask, ring, announcement.
 *
 * Mounted **exactly once** in `AppShell`, and outside the container with
 * `key={location.pathname}` — otherwise the component would be recreated on
 * every route change, i.e. precisely at the moment it starts working: the most
 * common trigger is a jump followed by a point.
 *
 * ## Why we search instead of using a ref
 *
 * The target is an arbitrary control anywhere in the tree — a button above a
 * table, a sidebar entry, a field in a dialog. Passing it up here via refs
 * would couple every one of those components to the guide. Instead the targets
 * carry an attribute (`data-guide-id`, package B4), and we search for it here.
 *
 * The search runs **repeatedly** via `requestAnimationFrame` until
 * `SPOTLIGHT_SEARCH_MS` has elapsed: after a navigation the target is almost
 * never there in the first frame, since the target page is still loading its
 * data. A single attempt would regularly miss. The deadline is also a
 * `setTimeout` rather than just a counter in the rAF loop — in jsdom (and in a
 * background tab) animation frames do not run at all, but giving up must still
 * happen.
 *
 * **Not found → show nothing.** The path is in the answer text anyway; a ring
 * around nothing would be worse than no ring. The request is discarded so that
 * it does not light up on the next page after all.
 *
 * ## We look for the first *visible* carrier of the id
 *
 * The same id regularly sits on several elements, all present in the document
 * at the same time:
 * - Header: the „Fragen" button appears twice, mobile (`sm:hidden`) and
 *   desktop (`max-sm:hidden`) — which one is visible is decided by a media
 *   query, not by rendering (`SiteHeader.tsx`).
 * - Tables: the create button above the table **and** the one in the empty
 *   state.
 * - On mobile the closed sidebar stays mounted (`MobileSidebar` in
 *   `components/motion/animated-sidebar.tsx`): it is merely `invisible`,
 *   `inert` and shifted out of view — so every `nav.*` target can be found.
 *
 * Singular `querySelector` would take the first of these in the document. For
 * a `display:none` element `getBoundingClientRect()` returns 0/0/0/0: the ring
 * would sit as a 12×12 blob in the top-left corner, the four rectangles would
 * darken the whole page, and `scrollIntoView` and `focus` would go nowhere.
 * So we search with `querySelectorAll` and take the first match that is
 * visible (`checkVisibility`, otherwise the parent chain), not `aria-hidden`
 * and not `inert` — **and** has a non-degenerate rectangle (`isMeasurable`; in
 * the browser this also drops anything that has not been laid out yet). If
 * there are only invisible matches, the rAF search continues — the sidebar may
 * still open, the table may still load.
 *
 * ## The target can also disappear again
 *
 * The most common flow of all: the spotlight starts right after navigation,
 * the target page first shows `TableEmptyState` and swaps it for the table 300
 * to 800 ms later — the pointed-at button is then a different node. A detached
 * element measures as 0/0/0/0; without a countermeasure the screen would stay
 * darkened until the four seconds run out. So we check `isConnected` — when
 * remeasuring and on a `SPOTLIGHT_ALIVE_MS` interval, because neither
 * `ResizeObserver` nor `scroll`/`resize` report a node disappearing.
 *
 * ## The z-axis is a trap
 *
 * `components/ui/dialog.tsx` documents the ladder: dialogs and sheets sit at
 * `z-[10050]`, popover/select/callout at `z-[10100]`, map overlays at
 * 9999–10001. A `z-50` would be silently invisible here. The mask sits at
 * `z-[10040]` — below dialogs, because it is meant to darken the *page*, not an
 * open dialog. It rises to `z-[10060]` as soon as the target is inside a
 * `[role="dialog"]` **or** any modal layer is open at all. The latter is the
 * main path of stage 2: on mobile the question panel itself is a sheet
 * (`AppShell.tsx`), and a spotlight on a target behind it would otherwise lie
 * entirely beneath its overlay.
 *
 * **Still open** (the panel consumer's job, not this file's): while the mobile
 * sheet is up, its overlay captures the pointer, and Radix' `hideOthers` has
 * disabled the rest of the tree via `aria-hidden` — including the live region
 * here. Whoever points from the mobile panel to a target *outside* it closes
 * the sheet first.
 *
 * ## What deliberately does *not* happen
 *
 * No `pointer-events`: mask and ring are fully click-through, the user can
 * press the pointed-at button immediately (and any other one too — the
 * spotlight is a hint, not a leash).
 *
 * No focus on arbitrary targets, and none the user currently needs for
 * themselves. Only `button`, `a[href]`, `input`, `textarea` and `select` are
 * focused (the last two because of prefill, CONTRACT.md §7.5), and even those
 * only while the user is not typing — the request typically comes from the
 * question panel, so the cursor sits in an input line, and yanking it out
 * would be worse than no focus. Also excluded are tabs (`role="tab"`, Radix
 * activates them on focus) and the targets in `WRITES_ON_CLICK_GUIDE_IDS`,
 * whose click creates something immediately: on a focused button the space
 * bar — pressed to scroll — is a write, and stage 2 writes nothing
 * (CONTRACT.md §7).
 *
 * ## Escape belongs to the ring
 *
 * The key listener runs in the **capture phase** and stops the event: an
 * Escape should remove the ring, not also close the dialog or the mobile
 * navigation sheet that contains the pointed-at target (both are Radix
 * `DismissableLayer`s listening on `document`). The second press goes through
 * again.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'motion/react';

import { cn } from '@/lib/utils';
import { WRITES_ON_CLICK_GUIDE_IDS } from './guideIds';
import { EASE_MORPH_OUT, type MorphEase } from './morph/morphTimeline';
import { clearSpotlight, isSpotlightFresh, useSpotlight } from './spotlight';
import './spotlight.css';

/** How long to search for the target (navigation + the target page's data fetch). */
export const SPOTLIGHT_SEARCH_MS = 1500;

/**
 * How long it stays lit. Four seconds are long enough to guide the eye and
 * short enough that nobody sits waiting for "that thing" to go away.
 */
export const SPOTLIGHT_SHOW_MS = 4000;

/** Gap between target and ring — the ring should not touch the element. */
export const SPOTLIGHT_PAD_PX = 6;

/**
 * What may pass through the attribute selector as a `data-guide-id`. Registry
 * ids use dot notation (`settings.apiKeys.create`); anything else — quotes,
 * square brackets, spaces — is rejected instead of being inserted into a
 * selector. `CSS.escape` is not available everywhere in jsdom, and an
 * allowlist is the stricter boundary here anyway.
 */
const SAFE_GUIDE_ID = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/;

/**
 * Targets that accept focus on their own — nothing else gets focused.
 * `TEXTAREA` and `SELECT` are included because prefill (CONTRACT.md §7.5)
 * points exactly there: `keywords` in the search profile is a multi-line
 * field, `metric`/`operator` of the alert rule are selects. Without them the
 * ring would light up but the cursor would not land in the field — the user
 * would have to click after all, exactly what stage 2 eliminates.
 */
const FOCUSABLE_TAGS = new Set(['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT']);

/**
 * How often to check whether the target is still attached to the document at
 * all. Neither `scroll`/`resize` nor the `ResizeObserver` report a node
 * disappearing — from then on a detached element measures as 0/0/0/0, and the
 * four rectangles would lay 28 % black over the whole page until the four
 * seconds run out. This case is the normal case: the target page first renders
 * `TableEmptyState` and swaps it out when the data arrives.
 */
export const SPOTLIGHT_ALIVE_MS = 250;

interface Box {
    left: number;
    top: number;
    width: number;
    height: number;
}

function measure(element: Element): Box {
    const rect = element.getBoundingClientRect();
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

/**
 * Does the rectangle have any extent at all? A degenerate one (0×0) comes from
 * an element that is in the document but occupies nothing — not laid out yet,
 * detached, or inside a container with `height: 0`. The ring would then sit as
 * a 12×12 blob (2 × `SPOTLIGHT_PAD_PX`) in the top-left corner and the four
 * rectangles would darken the whole page.
 *
 * Deliberately `or`, not `and`: a divider or a flat field legitimately has a
 * height of 0 but still a width.
 */
function isMeasurable(box: Box): boolean {
    return box.width > 0 || box.height > 0;
}

/**
 * Is any modal layer currently open? Radix sets `data-state="open"` on dialog
 * and sheet content; their overlay sits at `z-[10050]`. The layer must **not**
 * depend solely on whether the *target* is inside a dialog: on mobile the
 * question panel itself comes as a sheet (`AppShell.tsx`), and a spotlight on a
 * target behind it would otherwise lie entirely beneath its overlay — exactly
 * the main path of stage 2 (question in the panel, pointer onto the page).
 */
function hasOpenModal(): boolean {
    return document.querySelector('[role="dialog"][data-state="open"]') !== null;
}

/**
 * The target's accessible name, as far as it can be had here without a tree
 * walk: `aria-label`, otherwise the visible text. Only a fallback for a request
 * without a label — truncated so that a whole table row is not read aloud.
 */
function accessibleName(element: HTMLElement): string {
    const label = element.getAttribute('aria-label')?.trim();
    if (label) return label.slice(0, 120);
    return (element.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120);
}

/** The house easing curve as a CSS value — one number, one source. */
function cssEase(ease: MorphEase): string {
    return typeof ease === 'string' ? ease : `cubic-bezier(${ease.join(', ')})`;
}

function isFocusable(element: HTMLElement): boolean {
    if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') {
        return false;
    }
    // Radix tabs activate on focus (`activationMode` defaults to "automatic"):
    // pointing would switch the tab and write the URL — a question ("where do
    // I find…") would turn into a navigation.
    if (element.getAttribute('role') === 'tab') return false;
    if (element.tagName === 'A') return element.hasAttribute('href');
    return FOCUSABLE_TAGS.has(element.tagName);
}

/** True while the user is typing into a field — no focus change then. */
function isTyping(): boolean {
    const active = document.activeElement as HTMLElement | null;
    if (!active) return false;
    return active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable === true;
}

/**
 * Visibility, as far as it can be decided here. `checkVisibility` is the
 * precise way (it knows `display`, `visibility`, `content-visibility`); where
 * it is missing, we walk the parent chain. In jsdom every element has a 0×0
 * rectangle, so dimensions are useless as a criterion — tests set
 * `display:none`.
 *
 * `aria-hidden` and `inert` are added on top: the closed mobile sidebar
 * carries both, yet is in the document and even reports dimensions (it is
 * only shifted out of view).
 */
function isVisibleTarget(element: HTMLElement): boolean {
    if (!element.isConnected) return false;
    if (element.closest('[aria-hidden="true"]')) return false;
    if (element.closest('[inert]')) return false;
    const check = (element as { checkVisibility?: (options?: unknown) => boolean }).checkVisibility;
    if (typeof check === 'function') {
        return check.call(element, { checkVisibilityCSS: true, checkOpacity: false }) === true;
    }
    let node: HTMLElement | null = element;
    while (node) {
        const style = window.getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        node = node.parentElement;
    }
    return true;
}

export default function GuideSpotlight() {
    const request = useSpotlight();
    const { t } = useTranslation();
    const reducedMotion = useReducedMotion() === true;
    const [target, setTarget] = useState<HTMLElement | null>(null);
    const [box, setBox] = useState<Box | null>(null);
    const [elevated, setElevated] = useState(false);
    const [announcement, setAnnouncement] = useState('');

    /** Translation happens in the effect, not in render — so `t` belongs in no dependency list. */
    const tRef = useRef(t);
    tRef.current = t;

    /**
     * The motion preference is read when the target is found, not at render —
     * if it were in the search's dependencies, toggling it mid-load would
     * restart the search.
     */
    const reducedRef = useRef(reducedMotion);
    reducedRef.current = reducedMotion;

    // Search. `request` is identical between two requests (module store), so
    // it works as a dependency by itself.
    useEffect(() => {
        setTarget(null);
        setBox(null);
        setElevated(false);

        if (!request || !isSpotlightFresh(request)) return undefined;
        if (!SAFE_GUIDE_ID.test(request.guideId)) {
            clearSpotlight();
            return undefined;
        }

        let frame = 0;
        let deadline = 0;
        let stopped = false;

        const stop = () => {
            stopped = true;
            if (frame) window.cancelAnimationFrame(frame);
            if (deadline) window.clearTimeout(deadline);
        };

        const attempt = () => {
            if (stopped) return;
            const candidates = Array.from(
                document.querySelectorAll<HTMLElement>(`[data-guide-id="${request.guideId}"]`),
            );
            const found = candidates.find(
                (candidate) => isVisibleTarget(candidate) && isMeasurable(measure(candidate)),
            );
            if (!found) {
                // Even if there are matches: as long as none is visible and
                // measurable, there is nothing to show — keep searching until
                // the deadline. The table may still load, the sidebar may
                // still open.
                frame = window.requestAnimationFrame(attempt);
                return;
            }
            stop();
            // **Scroll first, then measure.** With reduced motion
            // `scrollIntoView` is instant and typically fires exactly *one*
            // `scroll` event — and it would fall into the window in which the
            // remeasure listener is not attached yet (it only arrives in the
            // next commit). Measured before the jump, the ring would sit at
            // the old spot and the top rectangle would cover the whole screen.
            // With smooth motion the follow-up events correct it anyway, but
            // even the first painted frame should be right.
            found.scrollIntoView?.({
                block: 'center',
                behavior: reducedRef.current ? 'auto' : 'smooth',
            });
            setTarget(found);
            setBox(measure(found));
            setElevated(Boolean(found.closest('[role="dialog"]')) || hasOpenModal());
            const writesOnClick = WRITES_ON_CLICK_GUIDE_IDS.indexOf(request.guideId) >= 0;
            if (!writesOnClick && !isTyping() && isFocusable(found)) {
                found.focus?.({ preventScroll: true });
            }
        };

        deadline = window.setTimeout(() => {
            stop();
            // Never showed up: discard it, don't leave it lying around.
            clearSpotlight();
        }, SPOTLIGHT_SEARCH_MS);
        attempt();

        return stop;
    }, [request]);

    /*
     * Presentation: end, cancel, remeasure.
     *
     * Depends on `target` **and** `request`. If a second request hits the same
     * element, `setTarget(null)` and `setTarget(found)` run in the same batch;
     * the final value is referentially equal to the previous one, React bails
     * out, and without `request` in the dependencies the timer of the **first**
     * request would keep running here — the second would inherit its remaining
     * time. Via a pointer click this heals itself (`pointerdown` cleans up
     * first), but not on the paths from the running stream (`highlight` block)
     * or via keyboard.
     */
    useEffect(() => {
        if (!target) return undefined;

        const finish = () => clearSpotlight();
        /**
         * First check whether the target is still attached at all: a detached
         * node measures as 0/0/0/0, the ring would jump to the top-left corner
         * and the mask would cover the whole screen.
         */
        const remeasure = () => {
            if (!target.isConnected) {
                finish();
                return;
            }
            setBox(measure(target));
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            // Consume it: otherwise the same press would close the dialog or
            // the mobile navigation sheet containing the pointed-at target.
            event.stopPropagation();
            event.preventDefault();
            finish();
        };

        const timer = window.setTimeout(finish, SPOTLIGHT_SHOW_MS);
        // Nobody reports the target disappearing: `ResizeObserver` no longer
        // fires for a detached node, and `scroll`/`resize` typically don't
        // occur at that moment. So check for it.
        const alive = window.setInterval(() => {
            if (!target.isConnected) finish();
        }, SPOTLIGHT_ALIVE_MS);
        // Measure once more after the commit: smooth `scrollIntoView` runs
        // over several frames, and an ancestor (sidebar, panel) may still
        // shift in the same commit.
        const settle = window.requestAnimationFrame(remeasure);
        // In the capture phase: the click should still reach its target (the
        // pointed-at button!), but the ring should vanish immediately.
        window.addEventListener('pointerdown', finish, true);
        window.addEventListener('keydown', onKeyDown, true);
        // When an *inner* container (table, dialog) scrolls, only the capture
        // phase reports it — `scroll` does not bubble.
        window.addEventListener('scroll', remeasure, true);
        window.addEventListener('resize', remeasure);
        const observer =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(remeasure);
        observer?.observe(target);

        return () => {
            window.clearTimeout(timer);
            window.clearInterval(alive);
            window.cancelAnimationFrame(settle);
            window.removeEventListener('pointerdown', finish, true);
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('scroll', remeasure, true);
            window.removeEventListener('resize', remeasure);
            observer?.disconnect();
        };
    }, [target, request]);

    /**
     * Write the announcement into the already-present live region **after**
     * the first paint. Mounting region and text in the same commit is exactly
     * the case NVDA, JAWS and VoiceOver regularly swallow: whatever is already
     * there on insertion is not a change to them. Mainly affected would be
     * targets that receive no focus (`settings.api.ingest` is a `div`) —
     * nothing at all would be announced there otherwise.
     *
     * **Announced even without a `label`.** The label is optional in the store
     * (`requestSpotlight({ guideId })` suffices), and the tool's `highlight`
     * block carries it but does not enforce it. An empty text would mean: ring
     * and dimming for sighted users, nothing for everyone else. The fallback is
     * therefore the accessible name of the found target and, last, a generic
     * sentence.
     */
    useEffect(() => {
        if (!request || !target) {
            setAnnouncement('');
            return;
        }
        const label = request.label || accessibleName(target);
        if (!label) {
            setAnnouncement(tRef.current('guide.spotlight.hereGeneric'));
            return;
        }
        setAnnouncement(
            request.hint
                ? tRef.current('guide.spotlight.hereWithHint', { label, hint: request.hint })
                : tRef.current('guide.spotlight.here', { label }),
        );
    }, [request, target]);

    /**
     * Mask and ring only exist while a target is measured; the live region
     * below is **always** in the document. Both therefore sit at fixed
     * positions in the fragment: if the region were sometimes the only child
     * and sometimes the second, React would unmount and remount it on the
     * switch — exactly what the effect above avoids.
     */
    function renderOverlay(measured: Box) {
        const hole = {
            left: Math.max(0, measured.left - SPOTLIGHT_PAD_PX),
            top: Math.max(0, measured.top - SPOTLIGHT_PAD_PX),
            width: Math.max(0, measured.width + 2 * SPOTLIGHT_PAD_PX),
            height: Math.max(0, measured.height + 2 * SPOTLIGHT_PAD_PX),
        };
        const right = hole.left + hole.width;
        const bottom = hole.top + hole.height;

        /** Above an open dialog (`z-[10050]`), otherwise below it. */
        const layer = elevated ? 'z-[10060]' : 'z-[10040]';
        const scrim = cn(
            'fixed pointer-events-none guide-spotlight-scrim',
            layer,
            !reducedMotion && 'guide-spotlight-enter',
        );
        const ease = { '--guide-spotlight-ease': cssEase(EASE_MORPH_OUT) } as CSSProperties;

        return (
            /* The mask is decoration: screen readers hear the announcement,
               not four empty rectangles. */
            <div aria-hidden="true">
                <div
                    data-testid="guide-spotlight-mask"
                    className={scrim}
                    style={{ ...ease, left: 0, top: 0, right: 0, height: hole.top }}
                />
                <div
                    data-testid="guide-spotlight-mask"
                    className={scrim}
                    style={{ ...ease, left: 0, top: bottom, right: 0, bottom: 0 }}
                />
                <div
                    data-testid="guide-spotlight-mask"
                    className={scrim}
                    style={{ ...ease, left: 0, top: hole.top, width: hole.left, height: hole.height }}
                />
                <div
                    data-testid="guide-spotlight-mask"
                    className={scrim}
                    style={{ ...ease, left: right, top: hole.top, right: 0, height: hole.height }}
                />
                <div
                    data-testid="guide-spotlight-ring"
                    className={cn(
                        'fixed pointer-events-none guide-spotlight-ring',
                        layer,
                        !reducedMotion && 'guide-spotlight-ring-pulse',
                    )}
                    style={{
                        ...ease,
                        left: hole.left,
                        top: hole.top,
                        width: hole.width,
                        height: hole.height,
                    }}
                />
            </div>
        );
    }

    // `isMeasurable` here too, not only when finding: between finding and
    // remeasuring the target can collapse (container folds up, data fetch
    // swaps the table). A degenerate rectangle would paint half the page dark
    // and a 12×12 ring in the corner — better nothing at all.
    const overlay = request && target && box && isMeasurable(box) ? renderOverlay(box) : null;

    return (
        <>
            {overlay}
            <div data-testid="guide-spotlight-status" className="sr-only" role="status" aria-live="polite">
                {announcement}
            </div>
        </>
    );
}
